import type { EventFragment } from "~/__generated__/graphql/graphql";

export const TICKET_VARIATION_PARAM = "ticketVariationID";

type TicketVariation = EventFragment["ticketVariations"][number];

export function getTicketVariations(plan: EventFragment): TicketVariation[] {
  return [...plan.ticketVariations].sort((a, b) => a.position - b.position);
}

export function getVisibleTicketVariations(
  plan: EventFragment,
): TicketVariation[] {
  return getTicketVariations(plan).filter((variation) => variation.available);
}

export function getSelectedTicketVariation(
  plan: EventFragment,
  variationID: string | null,
): TicketVariation | undefined {
  const variations = getTicketVariations(plan);
  if (variationID !== null) {
    return variations.find(
      (variation) => variation.id === variationID && variation.available,
    );
  }
  return variations.find((variation) => variation.available);
}

export function getDisplayedTicketPriceCents(
  plan: EventFragment,
  ticketVariationsEnabled: boolean,
): number | undefined {
  if (ticketVariationsEnabled) {
    const availableTotals = getTicketVariations(plan)
      .filter((variation) => variation.available)
      .map((variation) => variation.totalCostCents);
    return availableTotals.length > 0 ? Math.min(...availableTotals) : undefined;
  }
  return (
    plan.regularTicketCost?.totalCostCents ??
    plan.earlyBirdTicketCost?.totalCostCents ??
    undefined
  );
}

export function getTicketVariationAvailabilityLabel(
  variation: TicketVariation,
): string | null {
  const expired =
    variation.availabilityEndsAt !== null &&
    new Date(variation.availabilityEndsAt).getTime() <= Date.now();
  if (variation.archivedAt !== null || expired) return "Unavailable";
  if (variation.ticketsRemaining === 0) return "Sold out";
  if (!variation.available) return "Unavailable";
  return null;
}
